import css from '@styled-system/css';
import variant from '@styled-system/variant';
import { ThemeStyles } from '@honda-canada/design-system-react';

const themeStyles = new ThemeStyles();

const textBaseStyles = {
  // root Footer has fontFamily set
  fontFamily: 'inherit',
  fontSize: '14px',
  lineHeight: '24px',
  letterSpacing: 'sm',
  color: 'typographyDefault',
};

const linkBaseStyles = {
  display: 'inline-block !important',
  '&:hover': {
    borderColor: 'primary',
  },
  '&:focus': {
    borderColor: 'primary',
  },
};

themeStyles.addBaseStyles('FooterWrapper')(({ fontFamily }) =>
  css({
    fontFamily,
    position: 'relative',
    zIndex: 1,
    backgroundColor: 'white',
  }),
);

themeStyles.addBaseStyles('SecondaryMenuContainer')(({ paddingX }) =>
  css({
    paddingX,
  }),
);

themeStyles.addBaseStyles('DesktopWrapper')(({ theme, isHidden, paddingTop }) =>
  css({
    paddingTop,
    borderTop: `${theme.borders[1]} ${theme.colors.grey[3]}`,
    display: isHidden ? 'none' : 'block',
  }),
);

themeStyles.addBaseStyles('MobileContainer')(({ theme, isHidden, $styles, paddingX, paddingTop }) =>
  css({
    display: isHidden ? 'none' : 'block',
    ...$styles(theme),
    paddingTop,
    paddingX,
  }),
);

themeStyles.addBaseStyles('Container')(({ $maxWidth }) =>
  css({
    paddingX: ['m', 'm', 'm'],
    width: '100%',
    maxWidth: $maxWidth,
  }),
);

themeStyles.addBaseStyles('Row')(({ noWrap }) =>
  css({
    display: 'flex',
    flexWrap: noWrap ? undefined : 'wrap',
    marginX: ['m', 'm', 'zero'],
  }),
);

themeStyles.addBaseStyles('Column')(({ noFlex, display, columnGap }) =>
  css({
    paddingX: columnGap,
    flex: noFlex ? undefined : '1 0 0%',
    display: display || 'block',
    width: noFlex ? undefined : '100%',
    maxWidth: '100%',
  }),
);

themeStyles.addBaseStyles('SectionDivider')(({ theme }) =>
  css({
    borderTop: [theme.borders[1], theme.borders[1], theme.borders[2]],
    borderTopColor: ['grey.3', 'black', 'black'],
    mb: ['default', 'l', 'l'],
  }),
);

themeStyles.addBaseStyles('BottomLinksContainer')(({ theme, styles, bottomMenuReversed, paddingX, paddingY }) =>
  css({
    width: '100%',
    display: 'flex',
    flexDirection: bottomMenuReversed ? 'column-reverse' : 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: ['center', 'center', 'start'],
    ...styles,
    '> ul': {
      ...styles.ul,
      alignItems: 'center',
      justifyContent: 'center',
      '> li': {
        px: paddingX,
        py: paddingY,
        borderRight: [0, 0, `${theme.borders[1]} ${theme.colors.black}`],
      },
      '> li:last-child': {
        borderRightWidth: '0px',
      },
    },
  }),
);

themeStyles.addBaseStyles('SocialMediaContainer')(({ alignment = 'right', noPadding, rowGapDesktop = 'default' }) =>
  css({
    px: noPadding ? 0 : ['default', 'default', 0],
    paddingBottom: noPadding ? 0 : ['default', 'default', 0],
    display: 'flex',
    gap: ['default', 'default', rowGapDesktop],
    flexDirection: 'row',
    justifyContent: ['center', 'center', alignment],
  }),
);

themeStyles.addBaseStyles('SocialLink')(
  css({
    display: 'flex',
    border: 'none',
    '&:hover, &:focus': {
      border: 'none',
    },
  }),
);

themeStyles.addBaseStyles('LinkContainer')(
  ({ marginBottom }) =>
    css({
      marginBottom,
    }),
  ({ theme, borderLeft }) =>
    borderLeft &&
    css({
      paddingX: 'xs',
      borderLeft: theme.borders[1],
      borderLeftColor: 'grey.2',
      '&:first-child': {
        borderLeft: 'none',
      },
    }),
);

themeStyles.addBaseStyles('ItemLink')(({ lineHeight, fontSize }) =>
  css({
    ...textBaseStyles,
    ...linkBaseStyles,
    color: 'typographyDefault',
    lineHeight,
    fontSize,
  }),
);

themeStyles.addBaseStyles('ContactLink')(({ color, fontFamily }) =>
  css({
    ...textBaseStyles,
    ...linkBaseStyles,
    color,
    fontFamily,
  }),
);

themeStyles.addBaseStyles('BottomLink')(({ fontFamily, lineHeight, fontSize, color, marginX }) =>
  css({
    ...textBaseStyles,
    color: color ?? 'typographyDefault',
    fontFamily,
    fontSize: fontSize ?? 'sm',
    lineHeight: lineHeight ?? '22px',
    marginX,
    display: 'inline-block !important',
    '&:hover': {
      borderColor: 'primary',
    },
    '&:focus': {
      borderColor: 'primary',
    },
  }),
);

themeStyles.addBaseStyles('ListHeader')(({ fontFamily, lineHeight }) =>
  css({
    ...textBaseStyles,
    fontFamily,
    lineHeight,
    marginBottom: 'm',
  }),
);

themeStyles.addBaseStyles('MenuTitle')(({ fontFamily }) =>
  css({
    ...textBaseStyles,
    fontFamily,
    lineHeight: 'sm',
  }),
);

themeStyles.addBaseStyles('StyledCopy')(({ color }) =>
  css({
    ...textBaseStyles,
    color,
  }),
);

themeStyles.addBaseStyles('CopyRight')(({ lineHeight }) =>
  css({
    ...textBaseStyles,
    fontSize: 'sm',
    lineHeight: lineHeight ?? 'sm',
  }),
);

themeStyles.addBaseStyles('LinkList')(
  ({ noLastChild }) =>
    css({
      columnGap: 0,
      '&>li:last-child': noLastChild || {
        marginBottom: 0,
      },
    }),
  ({ flex }) =>
    flex &&
    css({
      display: ['flex', 'flex', 'flex'],
      flexWrap: 'nowrap',
      alignItems: 'center',
      justifyContent: 'center',
    }),
  ({ pullRight }) => pullRight && css({ marginLeft: ['auto', 'auto', 0] }),
);

themeStyles.addBaseStyles('BackToTopButton')(
  css({
    '>p': {
      lineHeight: '16px',
    },
  }),
);

const text = {
  fontFamily: 'default',
  fontWeight: 'regular',
  fontSize: '14px',
  lineHeight: ['22px', '22px', '24px'],
  letterSpacing: 'sm',
  color: 'typographyDefault',
};

themeStyles.addBaseStyles('UserLocationContainer')(({ styles }) =>
  css({
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    ...styles,
  }),
);

themeStyles.addBaseStyles('GeoLocatorContainer')(({ $padding }) =>
  css({
    display: 'block',
    paddingY: $padding ? 'default' : '0',
  }),
);

themeStyles.addBaseStyles('LocationWrapper')(({ mobileLocationTwoLines, marginLeft }) =>
  css({
    display: 'flex',
    alignItems: 'center',
    width: mobileLocationTwoLines ? ['100%', '100%', 'initial'] : undefined,
    marginLeft,
  }),
);

themeStyles.addBaseStyles('LocationLabel')(
  ({ fontFamily, fontSize, lineHeight, marginBottom }) =>
    css({
      ...text,
      fontSize,
      lineHeight,
      fontFamily,
      mr: 'xs',
      marginBottom,
    }),
  ({ showLabelOnMobile }) => {
    const mobileDisplay = showLabelOnMobile ? 'block' : 'none';

    return variant({
      prop: 'at',
      variants: {
        mobile: {
          display: ['block', 'block', 'none'],
        },
        desktop: {
          display: [mobileDisplay, mobileDisplay, 'block'],
        },
      },
    });
  },
);

themeStyles.addBaseStyles('IconWrapper')(() =>
  variant({
    prop: 'at',
    variants: {
      mobile: {
        display: ['flex', 'flex', 'none'],
      },
      desktop: {
        display: ['none', 'none', 'flex'],
      },
    },
  }),
);

themeStyles.addBaseStyles('Address')(({ marginRight, fontFamily, lineHeight }) =>
  css({
    ...text,
    flexShrink: 1,
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    mr: marginRight,
    fontFamily,
    lineHeight,
  }),
);

themeStyles.addBaseStyles('ButtonWrapper')(({ noMargin }) =>
  css({
    display: 'flex',
    alignItems: 'center',
    ml: noMargin ? 0 : ['auto', 'auto', 0],
  }),
);

themeStyles.addBaseStyles('ChangeLocationButton')(({ color, fontFamily, lineHeight, textDecoration }) =>
  css({
    fontFamily,
    whiteSpace: 'nowrap',
    color,
    lineHeight,
    textDecoration,
    border: 'none',
    '&:hover, &:focus': {
      border: 'none',
    },
  }),
);

themeStyles.addBaseStyles('BackToTopWrapper')(({ isHidden }) =>
  css({
    display: isHidden ? 'none !important' : 'block',
  }),
);

themeStyles.addBaseStyles('Logo')(({ theme, width, height }) =>
  css({
    pl: ['zero', 'zero', 's'],
    display: 'flex',
    alignItems: 'center',
    ...theme?.focused?.textLink(theme),
    img: {
      height: height || ['initial', 'initial', 'initial'],
      width: width || 'initial',
    },
  }),
);

themeStyles.addBaseStyles('TopContainer')(({ styles }) => css({ ...styles, mx: ['zero', 'zero', 's'] }));

themeStyles.addBaseStyles('MainContainer')(({ styles }) => css({ ...styles, pr: ['zero', 'zero', 's'] }));

themeStyles.addBaseStyles('SecondaryContainer')(({ styles }) => css({ ...styles, mx: ['zero', 'zero', 's'] }));

themeStyles.addBaseStyles('SecondaryContent')(({ showLocationAtSecondary }) =>
  css({
    display: 'flex',
    flexDirection: 'column',
    gap: 'default',
    flexWrap: showLocationAtSecondary ? 'wrap' : undefined,
  }),
);

themeStyles.addBaseStyles('SecondaryContentRow')(() =>
  css({
    display: 'flex',
  }),
);

themeStyles.addBaseStyles('BottomContainer')(({ styles }) =>
  css({
    display: 'flex',
    justifyContent: 'space-around',
    ...styles,
  }),
);

export default themeStyles;
